import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";
import { API_ENDPOINTS } from "./api-endpoints";
import useAuthStore from "@/store/use-auth-store";
import { clientSource } from "@/lib/client-source";
import i18n from "@/lib/i18n";
import { handlePaymentRequiredMiddleware } from "../middleware/payment-required-middleware";

// 🚀 TYPE EXTENSIONS for axios config
declare module "axios" {
  interface InternalAxiosRequestConfig {
    metadata?: {
      requestId: string;
    };
    __retryCount?: number;
  }
}

// Auth store'dan access token almak için bu fonksiyonu kullanacağız
// ⚠️ Migration fallback: Eski depolamada 'token' anahtarı vardı
const getAccessToken = (): string | null => {
  try {
    const storedState = localStorage.getItem("auth-storage");
    if (storedState) {
      const parsedState = JSON.parse(storedState);
      return parsedState.state?.accessToken || parsedState.state?.token || null;
    }
    return null;
  } catch (error) {
    console.error("Token alma hatası:", error);
    return null;
  }
};
// Auth store'dan refresh token almak için yardımcı
const getRefreshToken = (): string | null => {
  try {
    const storedState = localStorage.getItem("auth-storage");
    if (storedState) {
      const parsedState = JSON.parse(storedState);
      return parsedState.state?.refreshToken || null;
    }
    return null;
  } catch (error) {
    console.error("Refresh token alma hatası:", error);
    return null;
  }
};

// 🚀 RETRY CONFIGURATION
interface RetryConfig {
  maxRetries: number;
  retryDelay: (retryCount: number) => number;
  retryCondition: (error: AxiosError) => boolean;
}

const defaultRetryConfig: RetryConfig = {
  maxRetries: 3,
  retryDelay: (retryCount: number) =>
    Math.min(1000 * Math.pow(2, retryCount), 10000), // Exponential backoff: 1s, 2s, 4s max 10s
  retryCondition: (error: AxiosError) => {
    if (!error.response) return true; // Network errors
    const status = error.response.status;
    return status >= 500 || status === 408 || status === 429; // Server errors, timeout, rate limit
  },
};

// 🚀 TIMEOUT CONFIGURATION
interface TimeoutConfig {
  fast: number; // Quick operations like auth
  normal: number; // Normal API calls
  slow: number; // Heavy operations like file upload
  ai: number; // AI chat responses (longest)
}

const timeouts: TimeoutConfig = {
  fast: 5000, // 5 seconds
  normal: 15000, // 15 seconds
  slow: 30000, // 30 seconds
  ai: 60000, // 60 seconds for AI responses
};

// 🚀 REQUEST CANCELLATION MAP
const pendingRequests = new Map<string, AbortController>();

class ApiClient {
  private client: AxiosInstance;
  private refreshTokenPromise: Promise<string | null> | null = null;
  private lastRefreshAt: number = 0; // refresh cooldown için zaman damgası
  private readonly refreshCooldownMs: number = 5000; // 5 saniye
  private readonly proactiveLeewaySec: number = 45; // exp'e 45 sn kala yenile

  constructor(baseURL: string) {
    this.client = axios.create({
      baseURL,
      headers: {
        // Content-Type'ı multipart/form-data olarak değiştirdik
        // Axios otomatik olarak multipart/form-data için boundary ekleyecek
      },
      timeout: timeouts.normal, // Default timeout
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // İstek interceptor'ları
    this.client.interceptors.request.use(
      async (config) => {
        // 🎯 X-SOURCE HEADER: MiniApp veya Web platform bilgisi
        config.headers["x-source"] = clientSource();

        // 🌍 X-LOCALE HEADER: aktif dili backend'e ilet (i18n çevirileri için)
        if (!config.headers["x-locale"] && i18n.language) {
          config.headers["x-locale"] = i18n.language.split("-")[0];
        }

        // Token ekleme
        const token = getAccessToken();
        const urlPath = config.url || "";

        // Refresh/Login endpoint'lerine asla Authorization ekleme.
        // appleToken da buraya dahil: kullanıcı elinde eski/expired bir token
        // varken Apple ile giriş yapabilir; o isteğe Authorization eklemek
        // gereksiz bir proaktif refresh tetikler.
        if (
          urlPath.includes(API_ENDPOINTS.refreshToken) ||
          urlPath.includes(API_ENDPOINTS.login) ||
          urlPath.includes(API_ENDPOINTS.appleToken)
        ) {
          if (config.headers && "Authorization" in config.headers) {
            delete (config.headers as any).Authorization;
          }
        } else if (token) {
          // Proaktif refresh (leeway) + cooldown
          const isCoolingDown =
            Date.now() - this.lastRefreshAt < this.refreshCooldownMs;
          if (
            this.isTokenExpiringSoon(token) &&
            !this.refreshTokenPromise &&
            !isCoolingDown
          ) {
            try {
              await this.refreshToken();
            } catch {
              // Proaktif refresh başarısız olsa da isteği engellemeyelim
            }
          }

          // /ai/chat için Bearer, diğerlerinde raw token
          if (urlPath.includes("/api/v1/ai/chat")) {
            const latest = getAccessToken() || token;
            config.headers.Authorization = `${latest}`;
          } else {
            config.headers.Authorization = token;
          }
        }

        // 🚀 DYNAMIC TIMEOUT: Endpoint'e göre timeout belirleme
        const endpoint = config.url || "";
        if (endpoint.includes("/auth/") || endpoint.includes("/refresh")) {
          config.timeout = timeouts.fast;
        } else if (endpoint.includes("/upload") || endpoint.includes("/file")) {
          config.timeout = timeouts.slow;
        } else if (endpoint.includes("/chat")) {
          config.timeout = timeouts.ai;
        } else {
          config.timeout = timeouts.normal;
        }

        // 🚀 REQUEST CANCELLATION: Eşsiz request ID oluştur
        const requestId = `${config.method}-${config.url}-${Date.now()}`;
        const abortController = new AbortController();
        config.signal = abortController.signal;
        pendingRequests.set(requestId, abortController);

        // Request tamamlandığında map'ten çıkar
        config.metadata = { requestId };

        // FormData kullanılıyorsa Content-Type'ı otomatik ayarla
        if (config.data instanceof FormData) {
          // FormData için Content-Type'ı otomatik ayarla
          // Axios otomatik olarak multipart/form-data ve boundary ekleyecek
        } else {
          // JSON data için Content-Type'ı application/json olarak ayarla
          config.headers["Content-Type"] = "application/json";
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // Yanıt interceptor'ları
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        // 🚀 REQUEST CLEANUP: Başarılı response'dan sonra temizle
        const requestId = response.config?.metadata?.requestId;
        if (requestId && pendingRequests.has(requestId)) {
          pendingRequests.delete(requestId);
        }

        return response;
      },
      async (error: AxiosError) => {
        const config = error.config;

        // 🚀 REQUEST CLEANUP: Hata durumunda da temizle
        const requestId = config?.metadata?.requestId;
        if (requestId && pendingRequests.has(requestId)) {
          pendingRequests.delete(requestId);
        }

        // 🚀 TOKEN REFRESH: 401 hatası için önce refresh token'ı dene
        if (
          error.response?.status === 401 &&
          config &&
          typeof config.url === "string" &&
          !config.url.includes(API_ENDPOINTS.refreshToken) &&
          !config.url.includes(API_ENDPOINTS.login)
        ) {
          // 🔓 Public mod (guest): hiç oturum yoksa refresh/redirect deneme.
          // Public sayfalarda korumalı bir endpoint 401 dönerse kullanıcıyı
          // /login'e fırlatmak yerine hatayı çağırana bırakıyoruz
          // (auth gerektiren aksiyonlar AuthGateDialog ile korunuyor).
          const hadSession = !!(getAccessToken() || getRefreshToken());
          if (!hadSession) {
            return Promise.reject(error);
          }

          try {
            await this.refreshToken();
            // Token yenilendikten sonra orijinal isteği tekrar gönder
            return this.client.request(config!);
          } catch (refreshError) {
            // Token yenileme başarısızsa logout işlemi yapılabilir
            this.handleAuthError();
            return Promise.reject(refreshError);
          }
        }

        // 💳 PAYMENT REQUIRED: 402 durumunu merkezi middleware'de handle et
        if (error.response?.status === 402) {
          handlePaymentRequiredMiddleware({
            status: error.response.status,
            requestUrl: error.config?.url,
            body: error.response.data,
          });
          return Promise.resolve(error.response);
        }

        // ✅ CRITICAL: Diğer 4xx client errors için backend response'u döndür (retry yok!)
        // Backend'den gelen success: false ve error message'ları handle edebilmek için
        // 401'i hariç tuttuk çünkü yukarıda refresh token ile handle edildi
        if (
          error.response &&
          error.response.status >= 400 &&
          error.response.status < 500
        ) {
          // Response'u normal response gibi döndür (retry yapma!)
          return Promise.resolve(error.response);
        }

        // 🚀 RETRY LOGIC: Sadece 5xx server errors için retry yap
        if (this.shouldRetry(error, config)) {
          return this.retryRequest(error);
        }

        return Promise.reject(error);
      },
    );
  }

  // JWT exp yaklaşıyor mu?
  private isTokenExpiringSoon(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const exp: number | undefined = payload?.exp;
      if (!exp) return false;
      const nowSec = Math.floor(Date.now() / 1000);
      return exp - nowSec <= this.proactiveLeewaySec;
    } catch {
      return false;
    }
  }

  // 🚀 RETRY LOGIC IMPLEMENTATION
  private shouldRetry(error: AxiosError, config: any): boolean {
    if (!config || config.__retryCount >= defaultRetryConfig.maxRetries) {
      return false;
    }

    if (error.code === "ECONNABORTED" && error.message.includes("timeout")) {
      return true; // Timeout errors
    }

    return defaultRetryConfig.retryCondition(error);
  }

  private async retryRequest(error: AxiosError): Promise<AxiosResponse> {
    const config = error.config!;
    config.__retryCount = config.__retryCount || 0;
    config.__retryCount += 1;

    // 🚀 PROGRESSIVE TIMEOUT: Her retry'da timeout'u artır
    const baseTimeout = config.timeout || timeouts.normal;
    config.timeout = Math.min(
      baseTimeout * (1 + config.__retryCount * 0.5),
      timeouts.ai,
    );

    // Wait before retry
    const delay = defaultRetryConfig.retryDelay(config.__retryCount - 1);
    await new Promise((resolve) => setTimeout(resolve, delay));

    return this.client.request(config);
  }

  // 🚀 CANCEL ALL PENDING REQUESTS
  public cancelAllRequests(reason = "User navigation") {
    pendingRequests.forEach((controller) => {
      controller.abort(reason);
    });

    pendingRequests.clear();
  }

  // 🚀 CANCEL SPECIFIC REQUEST
  public cancelRequest(requestId: string) {
    const controller = pendingRequests.get(requestId);
    if (controller) {
      controller.abort("Request cancelled");
      pendingRequests.delete(requestId);
    }
  }

  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.get(url, config);
    return response.data;
  }

  public async post<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.client.post(
      url,
      data,
      config,
    );
    return response.data;
  }

  public async put<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.client.put(url, data, config);
    return response.data;
  }

  public async patch<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.client.patch(
      url,
      data,
      config,
    );
    return response.data;
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.delete(url, config);
    return response.data;
  }

  // 🚀 TOKEN REFRESH (OAuth2 - body: { refreshToken })
  private async refreshToken(): Promise<void> {
    // Halihazırda bir refresh işlemi varsa onu bekle
    if (this.refreshTokenPromise) {
      const token = await this.refreshTokenPromise;
      if (token) return;
      throw new Error("Token refresh failed");
    }

    // Yeni refresh işlemini başlat
    this.refreshTokenPromise = (async () => {
      try {
        const url = `${import.meta.env.VITE_API_BASE_URL}${
          API_ENDPOINTS.refreshToken
        }`;

        const refreshToken = getRefreshToken();
        if (!refreshToken) {
          throw new Error("No refresh token available");
        }

        const response = await axios.post(
          url,
          { refreshToken },
          {
            headers: {
              "Content-Type": "application/json",
            },
            timeout: timeouts.fast,
          },
        );

        const newAccessToken: string | undefined = (response as any)?.data?.data
          ?.accessToken;
        const newRefreshToken: string | undefined = (response as any)?.data
          ?.data?.refreshToken;

        if (!newAccessToken) {
          throw new Error("Refresh token response missing accessToken");
        }

        // Store'daki token'ları güncelle (persisted)
        if (typeof newRefreshToken === "string" && newRefreshToken.length > 0) {
          useAuthStore.getState().setTokens({
            accessToken: newAccessToken,
            refreshToken: newRefreshToken,
          });
        } else {
          useAuthStore.getState().setTokens({ accessToken: newAccessToken });
        }

        // Cooldown başlangıcı
        this.lastRefreshAt = Date.now();

        return newAccessToken;
      } catch (refreshError: any) {
        throw refreshError;
      } finally {
        this.refreshTokenPromise = null;
      }
    })();

    const token = await this.refreshTokenPromise;
    if (token) return;
    throw new Error("Token refresh failed");
  }

  // 🚀 AUTH ERROR HANDLER
  private handleAuthError(): void {
    // Clear auth state
    localStorage.removeItem("auth-storage");

    // Redirect to login (if in browser)
    if (typeof window !== "undefined") {
      window.location.href = "/login";
    }
  }
}

// AI API Client (apiai.chatharmony.ai)
export const aiApiClient = new ApiClient(import.meta.env.VITE_AI_API_BASE_URL);

// Regular API Client (api.chatharmony.ai/api/v1)
export const apiClient = new ApiClient(import.meta.env.VITE_API_BASE_URL);

// Onboarding API Client
export const onboardingApiClient = new ApiClient(
  import.meta.env.VITE_API_BASE_URL,
);
